import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { Calendar, Clock, User, Mail, Phone, MessageSquare } from 'lucide-react';

const Booking = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    piercingType: '',
    preferredDate: '',
    preferredTime: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const piercingTypes = [
    'Tongue', 'Lip', 'Monroe', 'Medusa', 'Nose', 'Septum', 'Eyebrow',
    'Ear Lobe', 'Cartilage', 'Industrial', 'Tragus', 'Daith', 'Rook',
    'Navel', 'Nipple', 'Surface Piercing', 'Dermal Anchor', 'Other'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { bookingAPI } = await import('../services/api');
      await bookingAPI.create(formData);
      
      toast({
        title: "Booking Request Received!",
        description: "We'll contact you shortly to confirm your appointment.",
      });
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        piercingType: '',
        preferredDate: '',
        preferredTime: '',
        message: ''
      });
    } catch (error) {
      console.error('Booking error:', error);
      toast({
        title: "Booking failed",
        description: error.response?.data?.detail || "Failed to submit booking. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 py-20 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-black mb-4 text-zinc-100">BOOK APPOINTMENT</h1>
          <p className="text-zinc-400 text-lg">
            Fill out the form below and we'll get back to you to confirm your appointment
          </p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <Label htmlFor="name" className="text-zinc-100 font-bold mb-2 flex items-center gap-2">
                <User className="h-4 w-4" />
                Full Name *
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                className="bg-zinc-950 border-zinc-700 text-zinc-100 focus:border-red-800"
                placeholder="John Doe"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email" className="text-zinc-100 font-bold mb-2 flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Email *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="bg-zinc-950 border-zinc-700 text-zinc-100 focus:border-red-800"
                placeholder="john@example.com"
              />
            </div>

            {/* Phone */}
            <div>
              <Label htmlFor="phone" className="text-zinc-100 font-bold mb-2 flex items-center gap-2">
                <Phone className="h-4 w-4" />
                Phone Number *
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                className="bg-zinc-950 border-zinc-700 text-zinc-100 focus:border-red-800"
                placeholder="(555) 123-4567"
              />
            </div>

            {/* Piercing Type */}
            <div>
              <Label htmlFor="piercingType" className="text-zinc-100 font-bold mb-2 block">
                Piercing Type *
              </Label>
              <Select
                required
                value={formData.piercingType}
                onValueChange={(value) => setFormData({ ...formData, piercingType: value })}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-100">
                  <SelectValue placeholder="Select piercing type" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-700">
                  {piercingTypes.map((type) => (
                    <SelectItem key={type} value={type} className="text-zinc-100">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date and Time */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="preferredDate" className="text-zinc-100 font-bold mb-2 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Preferred Date *
                </Label>
                <Input
                  id="preferredDate"
                  name="preferredDate"
                  type="date"
                  required
                  value={formData.preferredDate}
                  onChange={handleChange}
                  className="bg-zinc-950 border-zinc-700 text-zinc-100 focus:border-red-800"
                />
              </div>
              <div>
                <Label htmlFor="preferredTime" className="text-zinc-100 font-bold mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Preferred Time *
                </Label>
                <Input
                  id="preferredTime"
                  name="preferredTime"
                  type="time"
                  required
                  value={formData.preferredTime}
                  onChange={handleChange}
                  className="bg-zinc-950 border-zinc-700 text-zinc-100 focus:border-red-800"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <Label htmlFor="message" className="text-zinc-100 font-bold mb-2 flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Additional Notes (Optional)
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                className="bg-zinc-950 border-zinc-700 text-zinc-100 focus:border-red-800 min-h-[100px]"
                placeholder="Any questions or special requests?"
              />
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-red-800 hover:bg-red-700 text-white py-6 text-lg font-bold tracking-wide transition-all duration-300"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
            </Button>
          </form>

          <p className="text-zinc-500 text-sm text-center mt-6">
            * Required fields. We'll contact you within 24 hours to confirm your appointment.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Booking;